import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Building2, Check, ChevronsUpDown } from 'lucide-react'
import { useGetApiCompanies } from '@/api/generated/administrator-user/administrator-user'
import { switchCompany } from '@/api/axios-instance'
import { Button } from '@/components/shadcn/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { FilterSearch } from '@/components/common/filters/FilterSearch'
import { notify } from '@/lib/notify'
import { useAuthStore } from '@/store/auth.store'

/**
 * Lets a Bonisoft administrator pick the customer portal he works in. Stores the company, then
 * refreshes so the token carries the new CompanyId claim — every `/api/companies/current/...`
 * route reads that claim, so the whole portal follows without any page knowing about the switch.
 *
 * Renders only for the Administrator role; a company management user has exactly one company.
 */
export function CompanySwitcher() {
  const { t } = useTranslation('header')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const role = useAuthStore((s) => s.role)
  const companyId = useAuthStore((s) => s.companyId)
  const [isOpen, setIsOpen] = useState(false)
  const [isPending, setIsPending] = useState(false)
  const [search, setSearch] = useState('')

  const isAdministrator = role === 'Administrator'
  const { data } = useGetApiCompanies({}, { query: { enabled: isAdministrator } })

  if (!isAdministrator) {
    return null
  }

  const companies = data?.data ?? []
  const currentCompany = companies.find((c) => c.id === companyId)

  const query = search.trim().toLowerCase()
  const visible = query
    ? companies.filter(
      (c) =>
        c.name?.toLowerCase().includes(query) ||
        String(c.customerNumber ?? '').includes(query),
    )
    : companies

  // id === null leaves the company context: the endpoint is called without companyId, the new
  // token carries no CompanyId claim and the customer portal routes answer with 400 again — the
  // same state a fresh administrator is in.
  const handleSelect = async (id: number | null) => {
    if (id === companyId) {
      setIsOpen(false)
      return
    }

    setIsPending(true)
    try {
      // One call: the company lives in the tokens, so switching *is* a session renewal. It runs
      // through the refresh route because only there is a refresh token validated first.
      const switched = await switchCompany(id)
      if (!switched) {
        notify.error(t('companySwitcher.switchError'))
        return
      }

      // The cache still holds the previous company's data and the query keys don't contain the
      // company. reset (not clear) so mounted queries refetch instead of keeping stale data.
      await queryClient.resetQueries()

      setIsOpen(false)
      setSearch('')
      notify.success(
        id === null
          ? t('companySwitcher.cleared')
          : t('companySwitcher.switched', {
            name: companies.find((c) => c.id === id)?.name ?? '',
          }),
      )
      // Back to the landing route so the admin ends up on the new company's dashboard.
      navigate('/')
    } finally {
      setIsPending(false)
    }
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      {/* Tooltip outside, Popover inside: both triggers merge their props onto the same button. */}
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="max-w-56 justify-between gap-2 font-normal"
            >
              <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">
                {currentCompany?.name ?? t('companySwitcher.placeholder')}
              </span>
              <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{t('companySwitcher.tooltip')}</TooltipContent>
      </Tooltip>
      <PopoverContent align="start" className="w-80 p-2">
        <FilterSearch
          searchLabel={t('companySwitcher.searchLabel')}
          search={search}
          onChange={(v) => setSearch(v ?? '')}
          useDebounce={false}
        />
        <div className="mt-2 max-h-72 overflow-y-auto">
          {/* Only without a search term: this entry is not a company and would be confusing
              among filtered results. */}
          {!query && (
            <button
              type="button"
              disabled={isPending}
              onClick={() => handleSelect(null)}
              className="mb-1 flex w-full items-center gap-2 rounded-sm border-b px-2 py-1.5 pb-2 text-left text-sm hover:bg-accent disabled:opacity-50"
            >
              <Check
                className={`h-4 w-4 shrink-0 ${companyId === null ? 'opacity-100' : 'opacity-0'}`}
              />
              <span className="flex min-w-0 flex-col">
                <span className="truncate">{t('companySwitcher.none')}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {t('companySwitcher.noneHint')}
                </span>
              </span>
            </button>
          )}
          {visible.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              {t('companySwitcher.empty')}
            </p>
          ) : (
            visible.map((company) => (
              <button
                key={company.id}
                type="button"
                disabled={isPending}
                onClick={() => handleSelect(company.id)}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent disabled:opacity-50"
              >
                <Check
                  className={`h-4 w-4 shrink-0 ${company.id === companyId ? 'opacity-100' : 'opacity-0'}`}
                />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{company.name}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {t('companySwitcher.customerNumber', { number: company.customerNumber })}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
